import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  bounceDesktopOauthCallbackIfNeeded,
  completeProviderOAuth,
  isPopupOauthReturn,
  stripPopupOauthState,
} from "./shared/businessDigestSetup";
import {
  clearPerformerGmailOAuthReturnUrl,
  PERFORMER_CALENDAR_OAUTH_CONNECTED_EVENT,
  PERFORMER_CALENDAR_OAUTH_STATE,
  PERFORMER_DRIVE_OAUTH_STATE,
  PERFORMER_DRIVE_OAUTH_CONNECTED_EVENT,
  PERFORMER_GMAIL_OAUTH_CONNECTED_EVENT,
  PERFORMER_SLACK_OAUTH_CONNECTED_EVENT,
  PERFORMER_SLACK_OAUTH_STATE,
} from "./performerGmailOAuth";

const PERFORMER_OAUTH_BY_STATE = {
  performer_gmail: {
    provider: "gmail",
    purpose: "performer",
    connectedEvent: PERFORMER_GMAIL_OAUTH_CONNECTED_EVENT,
  },
  [PERFORMER_DRIVE_OAUTH_STATE]: {
    provider: "gmail",
    purpose: "drive",
    connectedEvent: PERFORMER_DRIVE_OAUTH_CONNECTED_EVENT,
  },
  [PERFORMER_CALENDAR_OAUTH_STATE]: {
    provider: "google_calendar",
    purpose: undefined,
    connectedEvent: PERFORMER_CALENDAR_OAUTH_CONNECTED_EVENT,
  },
  [PERFORMER_SLACK_OAUTH_STATE]: {
    provider: "slack",
    purpose: undefined,
    connectedEvent: PERFORMER_SLACK_OAUTH_CONNECTED_EVENT,
  },
};

/**
 * Exchange performer Gmail/Drive/Calendar/Slack OAuth codes on any business route.
 *
 * Attio CRM OAuth is handled by ``AttioConnectPanel`` (same pattern as digest
 * wizards), not here.
 *
 * The state carries a `_popup` marker (see `toPopupOauthState`) for every
 * performer connect flow -- they all open a disposable OAuth tab and poll
 * status from the opener (see `startPerformerGmailOAuthFlow` etc.), so once
 * the code is exchanged this tab has nothing left to show: it closes itself
 * instead of rendering a second, duplicate settings page.
 *
 * @example
 * // URL: /business/settings?code=abc&state=performer_gmail_popup
 * usePerformerOAuthCallback();
 * // POST /externalauth/gmail/callback, then window.close()
 */
export const usePerformerOAuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (bounceDesktopOauthCallbackIfNeeded()) {
      return undefined;
    }
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const rawState = params.get("state");
    const state = stripPopupOauthState(rawState);
    const oauthConfig = PERFORMER_OAUTH_BY_STATE[state];

    if (!code || !oauthConfig) {
      return undefined;
    }

    const isPopup = isPopupOauthReturn(rawState, state);

    let cancelled = false;
    const exchange = async () => {
      try {
        await completeProviderOAuth(oauthConfig.provider, code, {
          purpose: oauthConfig.purpose,
        });
        clearPerformerGmailOAuthReturnUrl();
        if (cancelled) return;
        if (isPopup) {
          window.close();
          return;
        }
        const next = new URLSearchParams(window.location.search);
        for (const key of ["code", "state", "scope"]) {
          next.delete(key);
        }
        const qs = next.toString();
        navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
        window.dispatchEvent(new CustomEvent(oauthConfig.connectedEvent));
      } catch (error) {
        console.error("Performer OAuth exchange failed:", error);
        if (isPopup) {
          window.close();
        }
      }
    };

    void exchange();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, navigate]);
};
